import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import api, { money, fmtDate } from "@/lib/api";
import { Page, PageHeader } from "@/components/Bits";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldCheck, Check, X, ChatCircleText } from "@phosphor-icons/react";
import ReviewNotes from "@/components/ReviewNotes";
import { useReturnToClose } from "@/hooks/useReturnToClose";

const KIND_LABEL = { setup: "Set-up", release: "Release", reversal: "Reversal" };

export default function Approvals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [approvingAll, setApprovingAll] = useState(false);
  const [counts, setCounts] = useState({});
  const [openNotes, setOpenNotes] = useState({});

  const loadCounts = useCallback(async () => {
    try {
      const { data } = await api.get("/comments/counts?entity_type=journal");
      setCounts(data.counts || {});
    } catch { /* noop */ }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/approvals");
      setItems(data.approvals || []);
      loadCounts();
    } finally { setLoading(false); }
  }, [loadCounts]);

  useEffect(() => { load(); }, [load]);
  useReturnToClose(items.length === 0, !loading);

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.post(`/approvals/${id}/approve`);
      toast.success("Approved & posted to Xero");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not approve");
    } finally { setBusy(null); }
  };

  const approveAll = async () => {
    setApprovingAll(true);
    try {
      const { data } = await api.post("/approvals/approve-all");
      if (data.approved > 0) toast.success(`Approved & posted ${data.approved} journal(s)`);
      if (data.skipped_count > 0) toast.warning(`${data.skipped_count} skipped (e.g. you requested them, or a locked period)`);
      if (data.approved === 0 && data.skipped_count === 0) toast.info("Nothing eligible to approve");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approve failed");
    } finally { setApprovingAll(false); }
  };

  const doReject = async () => {
    const id = rejecting;
    setBusy(id);
    try {
      await api.post(`/approvals/${id}/reject`, { reason });
      toast.success("Journal rejected");
      setRejecting(null); setReason("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reject");
    } finally { setBusy(null); }
  };

  return (
    <Page>
      <PageHeader overline="Two-person control" title="Approvals"
        subtitle="Journals waiting for a second person to approve before they post to Xero.">
        {items.length > 0 && (
          <span data-testid="approvals-count" className="inline-flex items-center gap-2 text-sm font-semibold text-warn bg-warn/10 border border-warn/30 px-3.5 py-2 rounded-full">
            <ShieldCheck size={16} weight="fill" /> {items.length} awaiting sign-off
          </span>
        )}
        {items.length > 1 && (
          <button onClick={approveAll} disabled={approvingAll} data-testid="approve-all-btn"
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-positive text-white hover:bg-navy transition-colors disabled:opacity-60">
            <Check size={16} weight="bold" /> {approvingAll ? "APPROVING…" : "Approve all eligible"}
          </button>
        )}
      </PageHeader>

      {loading ? (
        <div className="text-sm font-mono text-slate-600 py-10 tracking-widest">LOADING…</div>
      ) : items.length === 0 ? (
        <div className="card-surface p-14 text-center" data-testid="approvals-empty">
          <div className="w-14 h-14 rounded-2xl bg-positive/10 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck size={30} weight="fill" className="text-positive" />
          </div>
          <div className="font-heading font-bold text-xl text-navy">Nothing awaiting approval</div>
          <div className="text-sm text-slate-600 mt-1.5">When approval is enabled, due journals appear here for sign-off.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="approvals-list">
          {items.map((a, idx) => (
            <motion.div key={a.id} data-testid={`approval-${a.id}`}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: idx * 0.04 }}
              className="card-surface p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[0.62rem] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-orange/10 text-orangeink">{KIND_LABEL[a.kind] || a.kind}</span>
                    <span className="font-semibold text-navy text-sm">{a.doc_number} · {a.contact}</span>
                  </div>
                  <div className="text-sm text-slate-600 mt-1.5">{a.narration}</div>
                  <div className="font-mono text-xs text-slate-600 mt-1.5">
                    {fmtDate(a.date)} · requested by {a.requested_by} {a.requested_at ? `· ${fmtDate(a.requested_at)}` : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold text-navy tabular text-lg">{money(a.amount, a.currency)}</div>
                  <div className="flex items-center gap-2 mt-2">
                    <button onClick={() => setOpenNotes((o) => ({ ...o, [a.id]: !o[a.id] }))} data-testid={`approval-notes-toggle-${a.id}`}
                      className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border transition-colors ${openNotes[a.id] ? "border-orange text-orangeink bg-orange/5" : "border-slate-200 text-navy hover:bg-navy/5"}`}>
                      <ChatCircleText size={15} /> Notes{counts[a.id] ? ` · ${counts[a.id]}` : ""}
                    </button>
                    <button onClick={() => setRejecting(a.id)} disabled={busy === a.id} data-testid={`approval-reject-${a.id}`}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border border-slate-200 text-navy hover:border-negative hover:text-negative transition-colors disabled:opacity-60">
                      <X size={15} /> Reject
                    </button>
                    <button onClick={() => approve(a.id)} disabled={busy === a.id} data-testid={`approval-approve-${a.id}`}
                      className="flex items-center gap-1 px-4 py-1.5 text-sm font-semibold rounded-lg bg-positive text-white hover:bg-navy transition-colors disabled:opacity-60">
                      <Check size={15} weight="bold" /> {busy === a.id ? "…" : "Approve"}
                    </button>
                  </div>
                </div>
              </div>
              {openNotes[a.id] && (
                <div className="mt-4 pt-4 border-t border-slate-100" data-testid={`approval-notes-${a.id}`}>
                  <ReviewNotes entityType="journal" entityId={a.id} onChange={loadCounts} />
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}

      <Dialog open={!!rejecting} onOpenChange={(o) => !o && setRejecting(null)}>
        <DialogContent className="max-w-md rounded-2xl border-slate-200" data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl text-navy">Reject journal</DialogTitle>
            <DialogDescription className="text-navy/70">This journal will not post to Xero. Add an optional reason for the audit trail.</DialogDescription>
          </DialogHeader>
          <textarea value={reason} data-testid="reject-reason-input" onChange={(e) => setReason(e.target.value)}
            placeholder="Reason (optional)…" rows={3}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange focus:ring-2 focus:ring-orange/25 outline-none" />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => setRejecting(null)} className="px-4 py-2 text-sm rounded-lg border border-slate-200 text-navy hover:bg-navy/5">Cancel</button>
            <button onClick={doReject} disabled={busy} data-testid="reject-confirm-btn"
              className="px-5 py-2 text-sm font-medium rounded-lg bg-negative text-white hover:bg-navy transition-colors disabled:opacity-60">Reject</button>
          </div>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
